import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AuthService } from '../Security/auth.service';
import { IamService } from '../Security/iam.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userName: BehaviorSubject<string>;
  public userGuid: BehaviorSubject<string>;
  public emailAddress: BehaviorSubject<string>;
  public token: BehaviorSubject<string>;
  public roles: BehaviorSubject<string[]>;
  public isAdmin: BehaviorSubject<boolean>;
  public isInternal: BehaviorSubject<boolean>;
  public isInternalNonAdmin: BehaviorSubject<boolean>;
  public isExternal: BehaviorSubject<boolean>;
  public canEdit: BehaviorSubject<boolean>;
  public obtainingPermissions: BehaviorSubject<boolean>;

  public isAuthenticated: BehaviorSubject<boolean>;
  public errorObtainingPermissions: BehaviorSubject<boolean>;

  public permissions: any = null;

  public userType: UserType;

  private isReadOnly: BehaviorSubject<boolean>;

  constructor(
    private authService: AuthService,
    private iam: IamService,
  ) {
    this.isAdmin = new BehaviorSubject<boolean>(false);
    this.isInternal = new BehaviorSubject<boolean>(false);
    this.isReadOnly = new BehaviorSubject<boolean>(false);
    this.isInternalNonAdmin = new BehaviorSubject<boolean>(false);
    this.isExternal = new BehaviorSubject<boolean>(false);
    this.canEdit = new BehaviorSubject<boolean>(false);
    this.isAuthenticated = new BehaviorSubject<boolean>(false);
    this.obtainingPermissions = new BehaviorSubject<boolean>(false);
    this.errorObtainingPermissions = new BehaviorSubject<boolean>(false);

    this.userName = new BehaviorSubject<string>('');
    this.emailAddress = new BehaviorSubject<string>('');
    this.userGuid = new BehaviorSubject<string>('');
    this.token = new BehaviorSubject<string>('');
    this.roles = new BehaviorSubject<string[]>([]);

    this.iam.userPermission().subscribe(p => {
      this.permissions = p;
      this.setUserType();
    });
    this.iam.isObtainingPermission().subscribe(o => {
      this.obtainingPermissions.next(o);
    });
    this.iam.hasErrorObtainingPermission().subscribe(e => {
      this.errorObtainingPermissions.next(e);
    });
    this.authService.isAuthenticated().subscribe(a => {
      this.isAuthenticated.next(a);
    });

    this.authService.userGuid.subscribe(g => {
      this.userGuid.next(g);
    });
    this.authService.emailAddress.subscribe(e => {
      this.emailAddress.next(e);
    });
    this.authService.userName.subscribe(n => {
      this.userName.next(n);
    });
    this.authService.accessToken.subscribe(a => {
      this.token.next(a);
    });
  }

  public logout() {
    this.authService.logout();
  }

  public login(originalUrl: string) {
    this.authService.login(originalUrl);
  }

  setUserType() {
    if (this.permissions == null || this.permissions.results === null) {
      this.userType = UserType.UNKNOWN;
      this.isInternal.next(false);
      this.isExternal.next(false);
      this.isReadOnly.next(false);
      this.isInternalNonAdmin.next(false);
      this.canEdit.next(false);
      return;
    }
    this.userType = UserType.EXTERNAL;
    this.roles.next([this.permissions.results[0].role_name]);

    if (this.permissions.results[0].role_name === 'Admin') {
      this.userType = UserType.ADMIN;
      this.isAdmin.next(true);
      this.isInternal.next(true);
      this.isExternal.next(false);
      this.isReadOnly.next(false);
      this.isInternalNonAdmin.next(false);
      this.canEdit.next(true);
    }
    if (this.permissions.results[0].role_name === 'Read Only') {
      this.userType = UserType.READ_ONLY;
      this.isAdmin.next(false);
      this.isInternal.next(true);
      this.isExternal.next(false);
      this.isReadOnly.next(true);
      this.isInternalNonAdmin.next(true);
      this.canEdit.next(false);
    }
    if (this.permissions.results[0].role_name === 'User') {
      this.userType = UserType.EXTERNAL;
      this.isAdmin.next(false);
      this.isInternal.next(false);
      this.isExternal.next(true);
      this.isReadOnly.next(true);
      this.isInternalNonAdmin.next(false);
      this.canEdit.next(false);
    }
  }
}

export enum UserType {
  UNKNOWN,
  EXTERNAL,
  READ_ONLY,
  ADMIN,
}
